import { Suspense } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Building2, Sun, Moon, ShoppingBag, LogOut } from 'lucide-react';
import Logo from './Logo';
import CartDrawer from './CartDrawer';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import ErrorBoundary from '../ErrorBoundary';
import PageLoader from '../PageLoader';

/**
 * Layout de la sección mayorista. Es una sección aparte de la tienda, no una
 * categoría más: arriba no van las categorías (esas viven en la barra lateral
 * del catálogo mayorista), solo la vuelta a la tienda.
 */
export default function WholesaleLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { count, setIsOpen } = useCart();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="flex min-h-screen flex-col bg-mist-50 dark:bg-ink">
      <header className="sticky top-0 z-40 border-b border-ink/10 bg-mist-50/90 backdrop-blur-md dark:border-white/10 dark:bg-ink/90">
        <div className="mx-auto flex max-w-8xl items-center justify-between gap-4 px-5 py-4 sm:px-8 lg:px-12">
          {/* El logo ya es un link: envolverlo en otro anidaría dos <a> */}
          <div className="flex items-center gap-2">
            <Logo to="/mayorista" />
            <span className="hidden items-center gap-1.5 rounded-full bg-brand-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-brand-600 sm:inline-flex">
              <Building2 size={12} /> Mayorista
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} aria-label="Tema"
              className="grid h-10 w-10 place-items-center rounded-full hover:bg-black/5 dark:hover:bg-white/10">
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            {user && (
              <span className="hidden max-w-[160px] truncate text-sm text-ink/60 sm:block dark:text-mist-100/60">
                {user.wholesale?.companyName || user.name}
              </span>
            )}
            <button onClick={() => setIsOpen(true)} aria-label="Pedido"
              className="relative grid h-10 w-10 place-items-center rounded-full hover:bg-black/5 dark:hover:bg-white/10">
              <ShoppingBag size={19} />
              {count > 0 && (
                <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-brand-500 px-1 text-[10px] font-semibold text-white">
                  {count}
                </span>
              )}
            </button>
            {user && (
              <button onClick={handleLogout} title="Cerrar sesión" aria-label="Cerrar sesión"
                className="grid h-10 w-10 place-items-center rounded-full text-ink/60 hover:bg-black/5 hover:text-brand-500 dark:text-mist-100/60 dark:hover:bg-white/10">
                <LogOut size={19} />
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <ErrorBoundary key={pathname} name="WholesaleOutlet" compact>
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>

      <ErrorBoundary name="CartDrawer" silent>
        <CartDrawer />
      </ErrorBoundary>
    </div>
  );
}
